import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import './ExportProcess.css';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.1 } }),
};

const steps = [
  {
    num: '01', icon: '📩', title: 'Inquiry',
    desc: 'Share your requirements via our inquiry form, email, or WhatsApp. Include product name, quantity, destination country, and packaging preference.',
    details: ['Product & quantity specification', 'Destination country', 'Preferred packaging type', 'Delivery timeline'],
  },
  {
    num: '02', icon: '💰', title: 'Quotation',
    desc: 'We review your inquiry and send a detailed Proforma Invoice within 24–48 hours, including FOB/CIF pricing, specifications, and packaging details.',
    details: ['FOB / CIF pricing', 'Product specifications', 'Packaging details', 'Payment terms'],
  },
  {
    num: '03', icon: '🔬', title: 'Sampling',
    desc: 'On request, we can arrange pre-shipment product samples for your quality evaluation. Sample charges are adjusted against the final order.',
    details: ['Pre-shipment samples available', 'Lab test reports shared', 'Sample cost adjusted in order', 'Courier handled by us'],
  },
  {
    num: '04', icon: '✍️', title: 'Order Confirmation',
    desc: 'Once the sample is approved, you confirm the order. We accept payment via TT (Telegraphic Transfer), LC (Letter of Credit), or advance payment terms.',
    details: ['Purchase Order exchange', 'Payment: TT / LC / Advance', 'Contract finalization', 'Production scheduling'],
  },
  {
    num: '05', icon: '📦', title: 'Processing & Packaging',
    desc: 'Your order is processed under strict quality control, packed in export-grade packaging, and all necessary documents are arranged.',
    details: ['Quality check at every stage', 'Export-grade packaging applied', 'Labeling & marking as per buyer', 'All export docs prepared'],
  },
  {
    num: '06', icon: '🚢', title: 'Shipment & Delivery',
    desc: 'Goods are dispatched via sea freight or air freight based on your requirement. You receive a Bill of Lading and full shipment tracking details.',
    details: ['Sea / Air freight options', 'Bill of Lading shared', 'Full shipment tracking', 'Post-shipment docs dispatched'],
  },
];

const ExportProcess = () => {
  return (
    <main className="process-page">
      <section className="process-hero">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="section-tag" style={{ background: 'rgba(255,255,255,0.15)', color: 'var(--gold-light)' }}>
              Export Process
            </div>
            <h1 className="section-title" style={{ color: 'var(--white)', fontSize: 'clamp(30px,5vw,52px)' }}>
              From Inquiry to Your Port — A Seamless Journey
            </h1>
            <p className="section-sub" style={{ color: 'rgba(255,255,255,0.75)' }}>
              We make international sourcing simple, transparent, and reliable — every step of the way.
            </p>
          </motion.div>
        </div>
      </section>

      {/* STEPS */}
      <section style={{ padding: '80px 0', background: 'var(--white)' }}>
        <div className="container">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} style={{ marginBottom: '56px' }}>
            <div className="section-tag">Step by Step</div>
            <h2 className="section-title">Our 6-Step Export Journey</h2>
            <p className="section-sub">Clear, structured, and buyer-friendly — from first contact to final delivery.</p>
          </motion.div>

          <div className="steps-list">
            {steps.map((s, i) => (
              <motion.div
                key={s.num}
                className={`step-row ${i % 2 === 1 ? 'reverse' : ''}`}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i * 0.1}
                variants={fadeUp}
              >
                <div className="step-visual">
                  <div className="step-num-big">{s.num}</div>
                  <div className="step-icon-big">{s.icon}</div>
                </div>
                <div className="step-content">
                  <h3 className="step-title">{s.title}</h3>
                  <p className="step-desc">{s.desc}</p>
                  <ul className="step-details">
                    {s.details.map(d => <li key={d}>{d}</li>)}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PAYMENT */}
      <section style={{ padding: '80px 0', background: 'var(--off-white)' }}>
        <div className="container">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} style={{ marginBottom: '40px' }}>
            <div className="section-tag">Payment & Terms</div>
            <h2 className="section-title">Flexible Payment Options</h2>
          </motion.div>
          <div className="payment-grid">
            {[
              { icon: '🏦', title: 'TT (Telegraphic Transfer)', desc: 'Direct bank transfer. 30–50% advance, balance before shipment or against copy of BL.' },
              { icon: '📜', title: 'LC (Letter of Credit)', desc: 'Irrevocable LC at sight or usance. Standard international trade payment terms.' },
              { icon: '💳', title: 'Advance Payment', desc: '100% advance for smaller orders or new buyer relationships. Full support provided.' },
            ].map((p, i) => (
              <motion.div
                key={p.title}
                className="payment-card"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i * 0.1}
                variants={fadeUp}
              >
                <div style={{ fontSize: '36px', marginBottom: '14px' }}>{p.icon}</div>
                <h3 style={{ fontFamily: 'var(--ff-head)', fontSize: '20px', color: 'var(--green)', marginBottom: '10px' }}>{p.title}</h3>
                <p style={{ fontSize: '14px', color: 'var(--text-mid)', lineHeight: '1.6' }}>{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '72px 0', background: 'var(--green)', textAlign: 'center' }}>
        <div className="container">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2 style={{ fontFamily: 'var(--ff-head)', fontSize: '32px', color: 'var(--white)', marginBottom: '12px' }}>
              Ready to Start Your First Order?
            </h2>
            <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.7)', marginBottom: '28px' }}>
              Get in touch today and our team will guide you through every step.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/contact" className="btn-primary">Send an Inquiry →</Link>
              <a href="https://wa.me/919528480615" target="_blank" rel="noreferrer"
                style={{ background: '#25D366', color: '#fff', padding: '14px 28px', borderRadius: '10px', fontSize: '15px', fontWeight: 700, textDecoration: 'none' }}>
                💬 WhatsApp Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default ExportProcess;
